/**
 * Domain model for Sample Selection (Laboratory Specimen)
 * Represents a sample ready for selection in the laboratory processing workflow
 *
 * HU32-922499: Selección de muestras para procesamiento
 *
 * Aligned with backend SpecimenSelectionDTO:
 * - specimenId, specimenCode, lgCode
 * - concentration, ratio260280, ratio260230
 * - extractionMethod, physicalLocation, responsible
 * - technology, priority, isReprocess, extractionDate
 *
 * Note: Index signature added for TableComponent compatibility
 */
export interface SampleSelection {
  readonly specimenId: string;
  /**
   * Unique key for row-level selection in the work list table.
   * For MLPA/EXPANSION_TRIPLETAS with test subdivision: `${specimenId}_${testCode}`.
   * For all other specimens: equals `specimenId`.
   * Set by expandSpecimensByTests() before table rendering.
   */
  readonly selectionKey?: string;
  readonly specimenCode: string;
  readonly lgCode: string;
  readonly testCode?: string;
  readonly testCodes?: readonly string[];
  readonly testSuffixes?: readonly string[];
  readonly concentration: number;
  readonly ratio260280: number;
  readonly ratio260230: number;
  readonly extractionMethod: string;
  readonly physicalLocation: string;
  readonly responsible: string;
  readonly technology: string;
  readonly priority: string;
  readonly isReprocess: boolean;
  readonly extractionDate: Date;
  readonly [key: string]: unknown;
}

/**
 * Technology enum for laboratory samples
 * Values match backend enum exactly - DO NOT CHANGE
 */
export enum SampleTechnology {
  NGS = 'NGS',
  MICROARREGLOS = 'MICROARREGLOS',
  MLPA = 'MLPA',
  EXPANSION_TRIPLETAS = 'EXPANSION_TRIPLETAS'
}

/**
 * Priority enum for laboratory samples
 * Values match backend enum exactly - DO NOT CHANGE
 */
export enum SamplePriority {
  ADMINISTRATIVO = 'ADMINISTRATIVO',
  INSTITUCION = 'INSTITUCION',
  ESTANDAR = 'ESTANDAR',
  CLINICA = 'CLINICA'
}

/**
 * Laboratory metrics for dashboard
 */
export interface SampleSelectionMetrics {
  readonly date: string;
  readonly totalSamples: number;
  readonly selectedSamples: number;
  readonly reprocessSamples: number;
}

/**
 * @deprecated Use SampleSelection instead
 */
export type Specimen = SampleSelection;

/**
 * @deprecated Use SampleTechnology instead
 */
export const Technology = SampleTechnology;

/**
 * @deprecated Use SamplePriority instead
 */
export const Priority = SamplePriority;
